import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';

import { APP_CONFIG, AppConfig } from '@core/config';

import {
  ImportMethod,
  OrganisationType,
  ProjectTemplate,
  Region,
  RegionsResponse,
  Sector,
  SubscriptionsPrices,
} from '../models/configs.model';
import { BaseLayer, DataLayer } from '../models/layer.model';
import { PrintBorder } from '../models/print.model';
import { deserialise } from '../operators/deserialise.operator';
import { normalizeProjectTemplate } from '../utils/config.utils';

@Injectable({ providedIn: 'root' })
export class ConfigApiService {
  private readonly config = inject<AppConfig>(APP_CONFIG);
  private readonly http = inject(HttpClient);

  getLayers() {
    return this.http.post<{ basemaps: BaseLayer[]; layers: DataLayer[] }>(`${this.config.api.mapUrl}/config`, {});
  }

  getProjectTemplate(subtype: string) {
    return this.http
      .get<ProjectTemplate>(`${this.config.api.mapUrl}/project-definitions/${subtype}`)
      .pipe(deserialise(), map(normalizeProjectTemplate));
  }

  getProjectTemplates() {
    return this.http.get<ProjectTemplate[]>(`${this.config.api.mapUrl}/project-definitions`).pipe(
      deserialise(),
      map((templates) => templates.map(normalizeProjectTemplate))
    );
  }

  getSectors(withTemplates: boolean) {
    return this.http.get<Sector[]>(`${this.config.api.mapUrl}/sectors?withTemplates=${withTemplates}`).pipe(deserialise());
  }

  getOrganisationTypes() {
    return this.http.get<OrganisationType[]>(`${this.config.api.mapUrl}/organisation-types`).pipe(deserialise());
  }

  getSubscriptionsPrices() {
    return this.http.get<SubscriptionsPrices>(`${this.config.api.mapUrl}/users/subscriptions/price`).pipe(deserialise());
  }

  getPrintBorders() {
    return this.http.get<PrintBorder[]>(`${this.config.api.mapUrl}/prints/borders`).pipe(deserialise());
  }

  fetchRegions(): Observable<RegionsResponse> {
    return this.http
      .get<{ regions: { id: string; attributes: Region }[]; availableRegionIds: string[] }>(`${this.config.api.mapUrl}/regions`)
      .pipe(
        map((response) => ({
          ...response,
          regions: response.regions?.map(({ id, attributes }) => ({ ...attributes, id })),
        }))
      );
  }

  getImportMethods() {
    return this.http.get<ImportMethod[]>(`${this.config.api.mapUrl}/import-methods`).pipe(deserialise());
  }
}
